import React from 'react';
import {
  Card,
  Skeleton,
  Stack,
  SxProps,
  Typography,
  useTheme,
} from '@mui/material';
import {
  ProfileCardContent,
  LabelAndValueElement,
  ContextMenuButton,
  ContextMenu,
  ContextMenuItem,
  useApiClient,
  useFetchImmutable,
} from '@marageti/z4-lib';
import { BillingAddress } from '@marageti/z4-sdk/lib/travel/Partner';
import { CountryCodeMap } from '@marageti/z4-sdk/lib/standards';
import stylesT from '../styles';
import { formatCountryCodes } from '../../../../utils/helpers';

type AddressLabelAndValueElementProps = {
  title: string;
  country?: string;
  city?: string;
  street?: string;
  postalCode?: string;
  state?: string;
  titleSx?: SxProps;
  subtitleSx?: SxProps;
};

const AddressLabelAndValueElement = ({
  title,
  country,
  city,
  street,
  postalCode,
  state,
  titleSx = {},
  subtitleSx = {},
}: AddressLabelAndValueElementProps) => {
  const hasAddress = country || city || street || postalCode || state;

  return hasAddress ? (
    <Stack>
      <Typography sx={titleSx} variant="subtitle2">{title}</Typography>
      <Stack sx={subtitleSx}>
        {street && (
          <Typography variant="body1" color="text.secondary">
            {street}
          </Typography>
        )}
        {(city || state || postalCode) && (
          <Typography variant="body1" color="text.secondary">
            {[city, state, postalCode].filter(Boolean).join(', ')}
          </Typography>
        )}
        {country && (
          <Typography variant="body1" color="text.secondary">
            {country}
          </Typography>
        )}
      </Stack>
    </Stack>
  ) : (
    <Skeleton
      variant="rounded"
      animation="wave"
      sx={{
        margin: '0 auto',
        height: '44px',
        width: '100%',
      }}
    />
  );
};

type LabelAndValueListElementProps = {
  title: string;
  value: string[] | string | undefined;
  titleSx?: SxProps;
  subtitleSx?: SxProps;
};

const LabelAndValueListElement = ({
  title,
  value,
  titleSx = {},
  subtitleSx = {},
}: LabelAndValueListElementProps) => (value !== undefined ? (
  <Stack>
    <Typography sx={titleSx} variant="subtitle2">{title}</Typography>
    {Array.isArray(value) ? (
      <Stack>
        {value.map((email) => (
          <Typography key={email} sx={subtitleSx} variant="body1" color="text.secondary">
            {email}
          </Typography>
        ))}
      </Stack>
    ) : (
      <Typography sx={subtitleSx} variant="body1" color="text.secondary">
        {value}
      </Typography>
    )}
  </Stack>
) : (
  <Skeleton
    variant="rounded"
    animation="wave"
    sx={{
      margin: '0 auto',
      height: '44px',
      width: '100%',
    }}
  />
));

interface ProfileCardProps {
  contextMenuAnchor: HTMLElement | null;
  contextMenuOpen: boolean;
  onCloseContextMenu: () => void;
  onOpenContextMenu: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onOpenEditor: () => void;
  billingInfo: BillingAddress | undefined;
}

const BillingInfoCard = ({
  billingInfo,
  contextMenuAnchor,
  contextMenuOpen,
  onCloseContextMenu,
  onOpenContextMenu,
  onOpenEditor,
}: ProfileCardProps) => {
  const theme = useTheme();
  const styles = stylesT(theme);
  const menuId = 'billing_info-context-menu';
  const buttonId = 'billing-info-context-button';

  const apiClient = useApiClient();
  const { data: countryCodes } = useFetchImmutable<CountryCodeMap>(apiClient.standardsClient.getCountryCodes, [], true);
  const countryString = countryCodes && billingInfo && formatCountryCodes([billingInfo.country], countryCodes);

  return (
    <Card sx={styles.profileCard}>
      <ProfileCardContent>
        <Stack spacing="24px" width="100%">
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <LabelAndValueElement
              title="Billing Info"
              value={billingInfo?.name || '-'}
              key="billing-info"
            />
            <ContextMenuButton
              id={buttonId}
              open={contextMenuOpen}
              onClick={onOpenContextMenu}
              menuId={menuId}
            />
            <ContextMenu
              anchorEl={contextMenuAnchor}
              buttonId={buttonId}
              id={menuId}
              onClose={onCloseContextMenu}
              open={contextMenuOpen}
            >
              <ContextMenuItem onClick={onOpenEditor}>
                Edit
              </ContextMenuItem>
            </ContextMenu>
          </Stack>
          <LabelAndValueListElement
            title="Email"
            value={billingInfo?.emails || '-'}
            key="email"
          />
          <AddressLabelAndValueElement
            title="Address"
            country={countryString || '-'}
            city={billingInfo?.city}
            street={billingInfo?.street}
            postalCode={billingInfo?.postalCode}
            state={billingInfo?.state}
            key="address"
          />
        </Stack>
      </ProfileCardContent>
    </Card>
  );
};

export default BillingInfoCard;
